/**
 * useLivePricing — React hook
 *
 * Fetches live cloud pricing rates from the CloudCalculators Deno function
 * on mount. Returns a `liveRates` object (keyed by ProviderId) plus
 * loading / error state and a `refreshedAt` timestamp.
 *
 * Falls back to the static RATES table in pricing.ts if the fetch fails or
 * while loading — the calculator always shows numbers.
 */
import { useEffect, useState, useCallback } from 'react'
import type { ProviderId } from '@/lib/pricing'

export interface LiveRateRow {
  cpuHour: number
  ramHour: number
  storage: { standard: number; cold: number; archive: number }
  egress: number
  invocations: number
  live: boolean
  source: string
  fetchedAt?: string
}

export interface LivePricingState {
  rates: Partial<Record<ProviderId, LiveRateRow>>
  loading: boolean
  error: string | null
  refreshedAt: string | null
  cached: boolean
  refetch: () => void
}

const FUNCTION_URL =
  'https://api.lumi.new/v1/functions/p448283239112593408/cloudPricing'

export function useLivePricing(): LivePricingState {
  const [rates, setRates]           = useState<Partial<Record<ProviderId, LiveRateRow>>>({})
  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState<string | null>(null)
  const [refreshedAt, setRefreshedAt] = useState<string | null>(null)
  const [cached, setCached]         = useState(false)

  const fetchRates = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(FUNCTION_URL, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json() as {
        updatedAt: string
        rates: Record<string, LiveRateRow>
        cached: boolean
        error?: string
      }
      // Map the string keys from the function to typed ProviderId keys
      const typed: Partial<Record<ProviderId, LiveRateRow>> = {}
      for (const [key, val] of Object.entries(data.rates ?? {})) {
        typed[key as ProviderId] = val
      }
      setRates(typed)
      setRefreshedAt(data.updatedAt)
      setCached(data.cached ?? false)
      if (data.error) {
        // Partial degradation — log but don't throw
        console.warn('[useLivePricing] upstream error:', data.error)
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      setError(msg)
      console.error('[useLivePricing] fetch failed:', msg)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchRates()
  }, [fetchRates])

  return { rates, loading, error, refreshedAt, cached, refetch: fetchRates }
}
